/**
 * SkeletonLoading — shimmer placeholders shown while content loads from Supabase.
 * Used as an overlay on the page layout during initial content load.
 *
 * Provides multiple skeleton variants for different page types:
 * - PageSkeleton          — full template page (hero + content)
 * - TemplateCardSkeleton  — single dashboard template card
 * - DashboardPageSkeleton — full dashboard page (dark bg header + card grid)
 * - ProfilePageSkeleton   — profile page (avatar + form fields)
 * - ListPageSkeleton      — list/table pages (ActivityLog, ChangeHistory)
 * - AdminContentSkeleton  — generic admin content (ReviewCenter, FaceliftOverview)
 * - SettingsPageSkeleton   — settings page with tabs + form groups
 * - ClientBoardSkeleton   — client board page
 * - InlineSkeleton        — small inline content placeholder
 */

function Shimmer({ className = "" }: { className?: string }) {
  return (
    <div
      className={`bg-gray-200 rounded animate-pulse ${className}`}
      style={{ animationDuration: "1.5s" }}
    />
  );
}

/** Dark shimmer for dark-background contexts */
function DarkShimmer({ className = "" }: { className?: string }) {
  return (
    <div
      className={`bg-white/10 rounded animate-pulse ${className}`}
      style={{ animationDuration: "1.5s" }}
    />
  );
}

/** Full-page skeleton overlay with hero + content sections */
export function PageSkeleton() {
  return (
    <div className="min-h-screen" dir="rtl">
      {/* Hero skeleton */}
      <div className="bg-gradient-to-bl from-blue-50 via-white to-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center space-y-4">
          <Shimmer className="h-10 w-3/5 mx-auto" />
          <Shimmer className="h-5 w-2/5 mx-auto" />
          <Shimmer className="h-4 w-4/5 mx-auto" />
          <Shimmer className="h-4 w-3/5 mx-auto" />
          <div className="flex justify-center gap-4 pt-4">
            <Shimmer className="h-11 w-36 rounded-lg" />
            <Shimmer className="h-11 w-36 rounded-lg" />
          </div>
        </div>
      </div>

      {/* Section title skeleton */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center mb-10 space-y-3">
          <Shimmer className="h-7 w-48 mx-auto" />
          <Shimmer className="h-4 w-64 mx-auto" />
        </div>

        {/* Card grid skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-xl p-6 space-y-4">
              <Shimmer className="w-12 h-12 rounded-lg" />
              <Shimmer className="h-5 w-2/3" />
              <div className="space-y-2">
                <Shimmer className="h-3 w-full" />
                <Shimmer className="h-3 w-4/5" />
                <Shimmer className="h-3 w-3/5" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Second section skeleton */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center mb-10 space-y-3">
          <Shimmer className="h-7 w-56 mx-auto" />
          <Shimmer className="h-4 w-72 mx-auto" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="space-y-4">
              <Shimmer className="h-48 w-full rounded-xl" />
              <Shimmer className="h-5 w-3/4" />
              <Shimmer className="h-3 w-full" />
              <Shimmer className="h-3 w-2/3" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Inline skeleton for small content areas */
export function InlineSkeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className="space-y-2 py-2">
      {Array.from({ length: lines }).map((_, i) => (
        <Shimmer
          key={i}
          className={`h-3 ${i === lines - 1 ? "w-3/5" : "w-full"}`}
        />
      ))}
    </div>
  );
}

/** Dashboard template card skeleton */
export function TemplateCardSkeleton() {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
      <div className="flex items-center gap-3">
        <Shimmer className="w-10 h-10 rounded-lg" />
        <div className="flex-1 space-y-2">
          <Shimmer className="h-5 w-2/3" />
          <Shimmer className="h-3 w-1/3" />
        </div>
      </div>
      <div className="space-y-2">
        <Shimmer className="h-3 w-full" />
        <Shimmer className="h-3 w-4/5" />
      </div>
      <div className="flex gap-2 pt-2">
        <Shimmer className="h-8 w-20 rounded-lg" />
        <Shimmer className="h-8 w-20 rounded-lg" />
      </div>
    </div>
  );
}

/** Full dashboard page skeleton — dark header + toolbar + template card grid */
export function DashboardPageSkeleton() {
  return (
    <div className="min-h-screen" dir="rtl" style={{ background: "linear-gradient(135deg, #001C32 0%, #052b7a 100%)" }}>
      {/* Header area */}
      <div className="px-6 pt-6 pb-4">
        <div className="flex items-center justify-between mb-6">
          <DarkShimmer className="h-8 w-32" />
          <div className="flex gap-3">
            <DarkShimmer className="h-9 w-9 rounded-xl" />
            <DarkShimmer className="h-9 w-9 rounded-xl" />
          </div>
        </div>
        {/* Stats row */}
        <div className="flex gap-4 mb-6">
          {[1, 2, 3, 4].map((i) => (
            <DarkShimmer key={i} className="h-16 flex-1 rounded-xl" />
          ))}
        </div>
        {/* Toolbar */}
        <div className="flex items-center gap-3 mb-4">
          <DarkShimmer className="h-10 w-64 rounded-xl" />
          <DarkShimmer className="h-10 w-28 rounded-xl" />
          <div className="flex-1" />
          <DarkShimmer className="h-10 w-10 rounded-xl" />
          <DarkShimmer className="h-10 w-10 rounded-xl" />
        </div>
      </div>

      {/* Card grid */}
      <div className="px-6 pb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-3">
                <DarkShimmer className="w-10 h-10 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <DarkShimmer className="h-5 w-2/3" />
                  <DarkShimmer className="h-3 w-1/3" />
                </div>
              </div>
              <div className="space-y-2">
                <DarkShimmer className="h-3 w-full" />
                <DarkShimmer className="h-3 w-4/5" />
              </div>
              <div className="flex gap-2 pt-2">
                <DarkShimmer className="h-8 w-20 rounded-lg" />
                <DarkShimmer className="h-8 w-20 rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Profile page skeleton — header + avatar + form fields */
export function ProfilePageSkeleton() {
  return (
    <div className="min-h-screen" dir="rtl" style={{ background: "linear-gradient(135deg, #001C32 0%, #052b7a 100%)" }}>
      {/* Header */}
      <div className="px-6 py-4 flex items-center justify-between">
        <DarkShimmer className="h-8 w-28" />
        <DarkShimmer className="h-9 w-24 rounded-xl" />
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="bg-white rounded-2xl shadow-sm p-8 space-y-8">
          {/* Avatar + name section */}
          <div className="flex items-center gap-6">
            <Shimmer className="w-24 h-24 rounded-2xl" />
            <div className="flex-1 space-y-3">
              <Shimmer className="h-7 w-48" />
              <Shimmer className="h-4 w-32" />
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100" />

          {/* Form fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-2">
                <Shimmer className="h-4 w-24" />
                <Shimmer className="h-11 w-full rounded-xl" />
              </div>
            ))}
          </div>

          {/* More fields */}
          <div className="space-y-2">
            <Shimmer className="h-4 w-20" />
            <Shimmer className="h-24 w-full rounded-xl" />
          </div>

          {/* Action buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <Shimmer className="h-11 w-28 rounded-xl" />
            <Shimmer className="h-11 w-36 rounded-xl" />
          </div>
        </div>
      </div>
    </div>
  );
}

/** List/table page skeleton — for ActivityLog, ChangeHistory, etc. */
export function ListPageSkeleton() {
  return (
    <div className="min-h-screen" dir="rtl" style={{ background: "linear-gradient(135deg, #001C32 0%, #052b7a 100%)" }}>
      {/* Header */}
      <div className="px-6 py-4 flex items-center justify-between">
        <DarkShimmer className="h-8 w-40" />
        <div className="flex gap-2">
          <DarkShimmer className="h-9 w-9 rounded-xl" />
          <DarkShimmer className="h-9 w-24 rounded-xl" />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Toolbar */}
        <div className="flex items-center gap-3 mb-6">
          <Shimmer className="h-10 w-56 rounded-xl" />
          <Shimmer className="h-10 w-32 rounded-xl" />
          <div className="flex-1" />
          <Shimmer className="h-10 w-10 rounded-xl" />
        </div>

        {/* List items */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className={`flex items-center gap-4 px-5 py-4 ${i < 8 ? "border-b border-gray-100" : ""}`}>
              <Shimmer className="w-9 h-9 rounded-lg shrink-0" />
              <div className="flex-1 space-y-2">
                <Shimmer className="h-4 w-3/5" />
                <Shimmer className="h-3 w-2/5" />
              </div>
              <Shimmer className="h-6 w-16 rounded-full shrink-0" />
              <Shimmer className="h-3 w-20 shrink-0" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Admin content skeleton — generic admin/management page (ReviewCenter, FaceliftOverview, SeoManager) */
export function AdminContentSkeleton() {
  return (
    <div className="min-h-screen" dir="rtl" style={{ background: "linear-gradient(135deg, #001C32 0%, #052b7a 100%)" }}>
      {/* Header */}
      <div className="px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <DarkShimmer className="h-9 w-9 rounded-xl" />
          <DarkShimmer className="h-7 w-44" />
        </div>
        <div className="flex gap-2">
          <DarkShimmer className="h-9 w-28 rounded-xl" />
        </div>
      </div>

      {/* Content area */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[1, 2, 3].map((i) => (
            <Shimmer key={i} className="h-10 w-28 rounded-xl" />
          ))}
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shimmer className="w-10 h-10 rounded-lg" />
                  <div className="space-y-2">
                    <Shimmer className="h-5 w-40" />
                    <Shimmer className="h-3 w-24" />
                  </div>
                </div>
                <Shimmer className="h-6 w-16 rounded-full" />
              </div>
              <div className="space-y-2">
                <Shimmer className="h-3 w-full" />
                <Shimmer className="h-3 w-3/4" />
              </div>
              <div className="flex gap-2 pt-2">
                <Shimmer className="h-8 w-24 rounded-lg" />
                <Shimmer className="h-8 w-20 rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Settings page skeleton — tabs + grouped form fields */
export function SettingsPageSkeleton() {
  return (
    <div className="min-h-screen" dir="rtl" style={{ background: "linear-gradient(135deg, #001C32 0%, #052b7a 100%)" }}>
      {/* Header */}
      <div className="px-6 py-4 flex items-center justify-between">
        <DarkShimmer className="h-8 w-36" />
        <DarkShimmer className="h-9 w-24 rounded-xl" />
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <Shimmer key={i} className="h-10 w-24 rounded-xl" />
          ))}
        </div>

        {/* Settings groups */}
        {[1, 2].map((group) => (
          <div key={group} className="bg-white rounded-xl border border-gray-200 p-6 mb-6 space-y-5">
            <div className="flex items-center gap-2 mb-4">
              <Shimmer className="w-5 h-5 rounded" />
              <Shimmer className="h-5 w-36" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="space-y-2">
                  <Shimmer className="h-4 w-28" />
                  <Shimmer className="h-10 w-full rounded-lg" />
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Save button */}
        <div className="flex justify-end">
          <Shimmer className="h-11 w-40 rounded-xl" />
        </div>
      </div>
    </div>
  );
}

/** Client board skeleton */
export function ClientBoardSkeleton() {
  return (
    <div className="min-h-screen" dir="rtl" style={{ background: "linear-gradient(135deg, #001C32 0%, #052b7a 100%)" }}>
      {/* Header */}
      <div className="px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <DarkShimmer className="w-10 h-10 rounded-xl" />
          <div className="space-y-2">
            <DarkShimmer className="h-6 w-48" />
            <DarkShimmer className="h-3 w-32" />
          </div>
        </div>
        <div className="flex gap-2">
          <DarkShimmer className="h-9 w-9 rounded-xl" />
          <DarkShimmer className="h-9 w-28 rounded-xl" />
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 py-3 flex gap-2">
        {[1, 2, 3, 4].map((i) => (
          <DarkShimmer key={i} className="h-9 w-24 rounded-xl" />
        ))}
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-5">
        {/* Section cards */}
        {[1, 2, 3].map((sec) => (
          <div key={sec} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 space-y-4">
            <div className="flex items-center justify-between mb-3">
              <DarkShimmer className="h-6 w-40" />
              <DarkShimmer className="h-6 w-16 rounded-full" />
            </div>
            {/* Page items */}
            {[1, 2].map((p) => (
              <div key={p} className="bg-white/5 rounded-lg p-4 flex items-center gap-4">
                <DarkShimmer className="w-8 h-8 rounded-lg shrink-0" />
                <div className="flex-1 space-y-2">
                  <DarkShimmer className="h-4 w-2/3" />
                  <DarkShimmer className="h-3 w-1/3" />
                </div>
                <DarkShimmer className="h-7 w-20 rounded-lg shrink-0" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
